import { Component, Inject, OnInit } from '@angular/core';
import { type AbstractControl, FormBuilder, FormGroup, type ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ExportFormat,
  ExportPurpose,
  isValidEmail,
  isValidFileName,
  parseAndDeduplicateEmails,
} from '@cdw-ae/common-data';
import type { ExportCreationRequest } from '@cdw-ae/common-data';
import { HttpErrorResponse } from '@angular/common/http';
import { SnackbarService } from '@cdw-ae/snackbar';
import { CELL_LIMIT_WARNING_THRESHOLD, EXPORT_FORMAT_LIMITS } from './export-limits.constants';
import type { ExportDialogData, ExportDialogResult } from './export-dialog.interfaces';
import { ExportService } from './export.service';

@Component({
  selector: 'lib-cdw-export-dialog',
  templateUrl: './cdw-export-dialog.component.html',
  styleUrls: ['./cdw-export-dialog.component.scss'],
  standalone: false,
})
export class CdwExportDialogComponent implements OnInit {
  exportForm!: FormGroup;
  isSubmitting = false;
  cellCountWarning: string | null = null;
  cellCountExceeded = false;

  readonly formatOptions: { value: ExportFormat; label: string; disabled: boolean; tooltip?: string }[] = [];

  constructor(
    public dialogRef: MatDialogRef<CdwExportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ExportDialogData,
    private readonly fb: FormBuilder,
    private readonly exportService: ExportService,
    private readonly snackbarService: SnackbarService
  ) {
    this.formatOptions = this.buildFormatOptions();
  }

  readonly scopeOptions: { value: 'all' | 'selected'; label: string; disabled: boolean }[] = [];

  ngOnInit(): void {
    const now = new Date();
    const timestamp = this.formatTimestamp(now);
    const defaultFileName = `Export ${this.data.exportName} - ${timestamp}`;

    const defaultFormat = this.data.purpose === ExportPurpose.HUMAN_READABLE
      ? ExportFormat.GOOGLE_SHEETS
      : ExportFormat.CSV;

    const defaultScope: 'all' | 'selected' = this.data.selectedCount > 0 ? 'selected' : 'all';

    this.scopeOptions.push(
      { value: 'all', label: `All rows (${this.data.totalItemCount})`, disabled: false },
      {
        value: 'selected',
        label: `Selected rows (${this.data.selectedCount})`,
        disabled: this.data.selectedCount === 0,
      },
    );

    this.exportForm = this.fb.group({
      exportScope: [defaultScope, Validators.required],
      fileName: [defaultFileName, [Validators.required, this.validateFileName]],
      format: [defaultFormat, Validators.required],
      recipients: ['', [this.validateRecipientEmails]],
    });

    this.exportForm.get('format')?.valueChanges.subscribe(() => {
      this.checkCellLimit();
    });

    this.exportForm.get('exportScope')?.valueChanges.subscribe(() => {
      this.checkCellLimit();
    });

    this.checkCellLimit();
  }

  get currentScope(): 'all' | 'selected' {
    return this.exportForm?.get('exportScope')?.value ?? this.data.exportScope;
  }

  get itemCount(): number {
    return this.currentScope === 'selected'
      ? this.data.selectedCount
      : this.data.totalItemCount;
  }

  get columnCount(): number {
    const gridColumns = this.data.gridState.columnOrder?.filter(c => !c.hide && c.name !== 'selectCheckboxComponent' && c.name !== 'previewIconComponent').length ?? 0;
    const sidecarCount = this.data.sidecarColumns?.length ?? 0;
    return gridColumns + sidecarCount;
  }

  get estimatedCellCount(): number {
    return (this.itemCount + 1) * this.columnCount; // +1 for header row
  }

  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  onExport(): void {
    if (this.exportForm.invalid || this.isSubmitting) {
      return;
    }

    if (this.cellCountExceeded) {
      // Cell limit exceeded — do not proceed
      return;
    }

    this.isSubmitting = true;

    const formValue = this.exportForm.value;
    const recipients = this.parseRecipients(formValue.recipients);

    const scope: 'all' | 'selected' = formValue.exportScope;
    const isSelectedExport = scope === 'selected';
    // EDUS-2486 Hit-list #7: SSRM consumers (useSelection=true) don't
    // have a client-side id list — set useSelection on the request and
    // let the backend resolve from Redis. Legacy consumers continue
    // sending explicit selectedRowIds.
    const useServerSideSelection =
      isSelectedExport && this.data.useSelection === true;
    const gridState = {
      ...this.data.gridState,
      selectedRowIds:
        isSelectedExport && !useServerSideSelection
          ? this.data.gridState.selectedRowIds
          : undefined,
      filters: isSelectedExport ? undefined : this.data.gridState.filters,
    };

    const request: ExportCreationRequest = {
      gridState,
      purpose: this.data.purpose,
      queryType: this.data.queryType,
      format: formValue.format,
      fileName: formValue.fileName.trim(),
      recipients: [this.data.userEmail, ...recipients.filter(r => r !== this.data.userEmail)],
      customerId: this.data.customerId,
      fbUserId: this.data.fbUserId,
      dbUserId: this.data.dbUserId,
      userEmail: this.data.userEmail,
      userName: this.data.userName,
      ...(this.data.sidecarColumns?.length ? { sidecarColumns: this.data.sidecarColumns } : {}),
      ...(useServerSideSelection
        ? { useSelection: true, viewKey: this.data.viewKey }
        : {}),
      // EDUS-2698: forward the source audit log id for audit-log-changes exports.
      ...(this.data.auditLogId != null ? { auditLogId: this.data.auditLogId } : {}),
      // EDUS-2699: forward the target asset uuid + type for device-audit-history exports.
      ...(this.data.assetId != null ? { assetId: this.data.assetId } : {}),
      ...(this.data.assetTypeId != null ? { assetTypeId: this.data.assetTypeId } : {}),
    };

    this.exportService
      .initiateExport(
        request,
        useServerSideSelection ? this.data.gridSessionId : undefined
      )
      .subscribe({
      next: (response) => {
        this.isSubmitting = false;
        const result: ExportDialogResult = {
          success: response.success,
          exportId: response.exportId,
          format: formValue.format,
          exportScope: scope,
        };
        this.dialogRef.close(result);
      },
      error: (err: unknown) => {
        this.isSubmitting = false;
        let message = 'Export failed. Please try again.';

        if (err instanceof HttpErrorResponse) {
          if (err.status === 403) {
            message = 'You don\'t have permission to export. Contact your administrator.';
          } else if (err.error?.message) {
            // Use backend error message (e.g., domain-wide delegation error)
            message = err.error.message;
          }
        }

        // Domain-wide delegation errors require manual dismissal
        const isDwdError = message.includes('Domain-wide delegation not configured');

        this.snackbarService.showError(message, 'Dismiss', {
          duration: isDwdError ? 0 : 10000,
          horizontalPosition: 'center',
        });
      },
    });
  }

  get recipientsInvalid(): boolean {
    const control = this.exportForm?.get('recipients');
    return !!control?.invalid && !!control?.dirty;
  }

  get recipientsErrorMessage(): string {
    // biome-ignore lint/complexity/useLiteralKeys: ValidationErrors uses index signature, requiring bracket notation
    const emails: string[] = this.exportForm?.get('recipients')?.errors?.['invalidEmails']?.value ?? [];
    if (emails.length === 0) return 'Please enter valid email addresses, one per line.';
    const maxShown = 3;
    const shown = emails.slice(0, maxShown).join(', ');
    const remaining = emails.length - maxShown;
    return remaining > 0
      ? `Invalid email addresses: ${shown}, and ${remaining} more`
      : `Invalid email addresses: ${shown}`;
  }

  private validateFileName(control: AbstractControl): ValidationErrors | null {
    const value = control.value as string;
    if (!value?.trim()) {
      return null;
    }
    if (!isValidFileName(value)) {
      return { invalidFileName: true };
    }
    return null;
  }

  private validateRecipientEmails(control: AbstractControl): ValidationErrors | null {
    const value = control.value as string;
    if (!value?.trim()) {
      return null; // Empty is valid
    }
    const lines = value.split('\n').map((line: string) => line.trim()).filter((line: string) => line.length > 0);
    const invalidEmails = lines.filter((line: string) => !isValidEmail(line));
    if (invalidEmails.length > 0) {
      return { invalidEmails: { value: invalidEmails } };
    }
    return null;
  }

  private buildFormatOptions(): { value: ExportFormat; label: string; disabled: boolean; tooltip?: string }[] {
    return [
      {
        value: ExportFormat.GOOGLE_SHEETS,
        label: 'Google Sheets',
        disabled: false,
      },
      {
        value: ExportFormat.CSV,
        label: 'CSV',
        disabled: false,
      },
    ];
  }

  private checkCellLimit(): void {
    const format: ExportFormat = this.exportForm?.get('format')?.value;
    if (!format) {
      this.cellCountWarning = null;
      this.cellCountExceeded = false;
      return;
    }

    const limit = EXPORT_FORMAT_LIMITS[format];
    if (!limit) {
      this.cellCountWarning = null;
      this.cellCountExceeded = false;
      return;
    }

    const cells = this.estimatedCellCount;

    if (cells > limit.maxCells) {
      this.cellCountExceeded = true;
      this.cellCountWarning = `Estimated cell count (${cells.toLocaleString()}) exceeds the ${limit.displayName} limit of ${limit.maxCells.toLocaleString()} cells. Please reduce the number of rows or columns.`;
    } else if (cells > limit.maxCells * CELL_LIMIT_WARNING_THRESHOLD) {
      this.cellCountExceeded = false;
      this.cellCountWarning = `Estimated cell count (${cells.toLocaleString()}) is approaching the ${limit.displayName} limit of ${limit.maxCells.toLocaleString()} cells.`;
    } else {
      this.cellCountWarning = null;
      this.cellCountExceeded = false;
    }
  }

  private parseRecipients(raw: string): string[] {
    return parseAndDeduplicateEmails(raw);
  }

  private formatTimestamp(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}_${month}_${day}-${hours}_${minutes}_${seconds}`;
  }
}
